import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, HeadphonesIcon } from 'lucide-react';
type Message = {
  id: string;
  text: string;
  sender: 'bot' | 'user';
  timestamp: Date;
  isActionable?: boolean;
};
type ResponseRule = {
  keywords: string[];
  response: string;
};
const RESPONSE_RULES: ResponseRule[] = [{
  keywords: ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'howdy', 'sup', "what's up", 'greetings'],
  response: 'Hello! 👋 Welcome to Billvast Support. How can I help you today?'
}, {
  keywords: ['account', 'kyc', 'verify', 'verification', 'tier', 'upgrade', 'identity', 'id card', 'bvn', 'nin', 'document'],
  response: "Billvast has 3 KYC tiers:\n\n• Tier 1 (Basic) — Unverified, limited access\n• Tier 2 (Standard) — ₦200,000 daily limit\n• Tier 3 (Premium) — Unlimited transactions\n\nYou'll need a valid photo ID, BVN, and proof of address. Go to Settings → KYC to upgrade."
}, {
  keywords: ['wallet', 'balance', 'fund', 'add money', 'top up', 'deposit', 'card funding'],
  response: 'Your Billvast Wallet supports multiple funding methods:\n\n💳 Debit/Credit Card (Visa, Mastercard, Verve)\n📱 USSD Banking\n🏦 Bank Transfer\n📲 Google Pay, Apple Pay, Mobile Pay\n\nMinimum deposit is ₦1,000. Go to Wallet → Add Funds to get started.'
}, {
  keywords: ['exchange', 'currency', 'rate', 'forex', 'dollar', 'pound', 'euro', 'convert', 'usd', 'gbp', 'eur', 'cad', 'aud', 'cedi', 'rand', 'shilling'],
  response: 'We support 20+ currencies across International and African markets:\n\n🌍 International: USD, GBP, EUR, CAD, AUD, CHF, JPY, CNY, AED, INR\n🌍 African: ZAR, GHS, KES, XOF, EGP, TZS, UGX, RWF, XAF, MAD\n\nRates are updated in real-time. Visit the Exchange page to convert.'
}, {
  keywords: ['bill', 'airtime', 'electricity', 'cable', 'tv', 'dstv', 'gotv', 'internet', 'wifi', 'data', 'mtn', 'airtel', 'glo', '9mobile', 'spectranet', 'utility'],
  response: 'Pay all your bills from one dashboard:\n\n📱 Airtime & Data (MTN, Airtel, Glo, 9mobile)\n⚡ Electricity (Ikeja, Eko, Abuja, Kano)\n📺 Cable TV (DSTV, GOTV, Startimes)\n🌐 Internet (Spectranet, Smile, FibreOne)\n💧 Water & 🏢 Service Charges\n\nGo to Bill Payment to get started.'
}, {
  keywords: ['water', 'water bill', 'lagos water', 'fct water'],
  response: 'You can pay water bills from these providers:\n\n💧 Lagos Water Corp\n💧 FCT Water Board\n💧 Ogun State Water Corp\n💧 Rivers State Water\n\nGo to Bill Payment → Water to pay.'
}, {
  keywords: ['service charge', 'estate', 'oceanbay', 'lekki palm', 'victoria crest', 'royal pine', 'banana island', 'eko atlantic', 'pinnock', 'chevron estate', 'magodo', 'vgc'],
  response: 'Pay estate service charges for:\n\n🏘️ Oceanbay Estate\n🏘️ Lekki Palm City\n🏘️ Victoria Crest\n🏘️ Royal Pine Estate\n🏘️ Banana Island Ikoyi\n🏘️ Eko Atlantic\n🏘️ Pinnock Beach Estate\n🏘️ Chevron Estate Lekki\n🏘️ And more!\n\nGo to Bill Payment → Service Charge.'
}, {
  keywords: ['crowdfund', 'donate', 'campaign', 'cause', 'fundraise', 'raise money', 'charity', 'gofundme', 'medical fund', 'community'],
  response: 'Billvast Crowdfunding features:\n\n❤️ Zero platform fees for social causes\n✅ Verified & transparent campaigns\n📊 Real-time donation tracking\n📱 QR code donation links\n🔗 Social media sharing\n\nStart a campaign or donate at the Crowdfunding page.'
}, {
  keywords: ['facility', 'rent', 'maintenance', 'property', 'landlord', 'tenant', 'lease', 'repair'],
  response: 'Facility Management features:\n\n🏠 Rent collection & payment reminders\n🔧 Maintenance request tracking\n📄 Lease document management\n💬 Tenant-landlord messaging\n📊 Property portfolio overview\n\nAccess it via Bill Payment → Facility Mgmt tab.'
}, {
  keywords: ['transfer', 'send money', 'beneficiary', 'remittance', 'wire'],
  response: 'Send money locally or internationally:\n\n🏦 Transfer to any Nigerian bank\n🌍 International remittance\n👥 Save beneficiaries for quick repeat transfers\n📋 Track all transfers in History\n\nGo to the Exchange page to send money.'
}, {
  keywords: ['security', 'password', '2fa', 'two-factor', 'authentication', 'pin', 'biometric', 'fingerprint', 'face id', 'protect', 'safe'],
  response: 'Your security options:\n\n🔐 Two-Factor Authentication (2FA)\n📱 Authenticator App\n💬 SMS Verification\n📧 Email Verification\n👆 Biometric (Fingerprint/Face ID)\n🔑 Transaction PIN\n\nEnable these in Settings → Security.'
}, {
  keywords: ['signup', 'sign up', 'register', 'create account', 'join', 'new account', 'open account'],
  response: 'To create a Billvast account:\n\n1️⃣ Click "Launch App" or go to the Sign Up page\n2️⃣ Enter your name, email, phone & password\n3️⃣ Complete KYC verification\n4️⃣ You must be 18+ with a valid ID\n\nIndividuals need: Photo ID, BVN, proof of address.\nOrganizations need: CAC certificate, BVN, office address.'
}, {
  keywords: ['login', 'log in', 'sign in', "can't login", 'forgot password', 'reset password', 'locked out'],
  response: 'To log in, go to the Login page and enter your email and password.\n\nIf you forgot your password, use the "Forgot Password" option to reset it via email.\n\nStill having trouble? Type "agent" to connect with our support team.'
}, {
  keywords: ['fee', 'charge', 'cost', 'price', 'pricing', 'how much', 'commission', 'free'],
  response: 'Billvast pricing:\n\n✅ Crowdfunding — Zero platform fees\n💱 Exchange — 1.5% transfer fee\n📱 Bill Payments — No extra charges\n💰 Wallet Funding — Free\n🏦 Bank Transfers — Standard bank charges apply\n\nWe believe in transparent, fair pricing.'
}, {
  keywords: ['receipt', 'statement', 'download', 'pdf', 'print', 'transaction history', 'export'],
  response: 'You can generate receipts and statements from your Wallet:\n\n📄 Transaction receipts (PDF & PNG)\n📊 Account statements with date range\n🔏 Official Billvast stamp & signature\n📥 Download or print anytime\n\nGo to Wallet → select a transaction → Download Receipt.'
}, {
  keywords: ['contact', 'email', 'phone', 'call', 'reach', 'address', 'office', 'location'],
  response: 'Contact Billvast:\n\n📧 Email: support@billvast.com\n📞 Phone: +234 800 BILLVAST\n🏢 Office: Lagos, Nigeria\n💬 Live Chat: Type "agent" here\n\nOur support team is available Mon-Sat, 8am-8pm WAT.'
}, {
  keywords: ['about', 'what is billvast', 'who are you', 'company', 'billvast'],
  response: "Billvast is a Nigerian fintech ecosystem with 3 core pillars:\n\n🤝 Community Crowdfunding — Zero-fee fundraising\n💱 Foreign Exchange — 20+ currencies\n📱 Bills & Facility Management — Utilities, rent & more\n\nWe're building the ultimate financial companion for Nigerians everywhere."
}, {
  keywords: ['thank', 'thanks', 'thank you', 'appreciate', 'great', 'awesome', 'nice', 'cool', 'perfect', 'good job'],
  response: "You're welcome! 😊 Is there anything else I can help you with? Feel free to ask anytime."
}, {
  keywords: ['bye', 'goodbye', 'see you', 'later', 'close', 'done', "that's all", 'no more'],
  response: 'Goodbye! 👋 Thank you for chatting with Billvast Support. Have a wonderful day! Feel free to come back anytime.'
}, {
  keywords: ['help', 'assist', 'how to', 'guide', 'tutorial', 'instructions', 'what can you do', 'options'],
  response: 'I can help you with:\n\n🔹 Account & KYC verification\n🔹 Wallet & payments\n🔹 Currency exchange (20+ currencies)\n🔹 Bill payments & service charges\n🔹 Crowdfunding & donations\n🔹 Facility management\n🔹 Security settings\n🔹 Receipts & statements\n\nJust type your question or tap a quick action!'
}, {
  keywords: ['complain', 'complaint', 'issue', 'problem', 'bug', 'error', 'not working', 'broken', 'failed', 'stuck', 'wrong'],
  response: "I'm sorry you're experiencing an issue. 😔 Here's what you can do:\n\n1️⃣ Try refreshing the page\n2️⃣ Clear your browser cache\n3️⃣ Check your internet connection\n\nIf the problem persists, type \"agent\" to connect with a live support agent who can investigate further."
}, {
  keywords: ['refund', 'reverse', 'chargeback', 'money back', 'wrong transfer', 'wrong payment'],
  response: 'For refund or reversal requests:\n\n1️⃣ Note your transaction reference number\n2️⃣ Contact our support team immediately\n3️⃣ Refunds are processed within 24-72 hours\n\nType "agent" to connect with a live agent for immediate assistance with your refund.'
}];
const QUICK_ACTIONS = ['Account & KYC', 'Wallet & Payments', 'Exchange Rates', 'Bill Payment', 'Crowdfunding', 'Help', 'Talk to Agent'];
export const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    text: 'Hello! 👋 Welcome to Billvast Support. How can I help you today?',
    sender: 'bot',
    timestamp: new Date(),
    isActionable: true
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(1);
  const [isLiveAgentMode, setIsLiveAgentMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [messages, isOpen]);
  const generateBotResponse = (userText: string): string => {
    const lowerText = userText.toLowerCase().trim();
    if (isLiveAgentMode) {
      return 'An agent will be with you shortly. Please hold on. ⏳';
    }
    // Live agent trigger
    if (['agent', 'human', 'live agent', 'talk to agent', 'real person', 'operator', 'representative'].some(k => lowerText.includes(k))) {
      setIsLiveAgentMode(true);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: "You've been added to the queue. A support agent will be with you shortly. Average wait time: ~3 minutes.\n\n📧 support@billvast.com\n📞 +234 800 BILLVAST",
          sender: 'bot',
          timestamp: new Date()
        }]);
        setIsTyping(false);
      }, 2000);
      return 'Connecting you to a live agent... 🔄';
    }
    // Match against rules - find best match (most keyword hits)
    let bestMatch: ResponseRule | null = null;
    let bestScore = 0;
    for (const rule of RESPONSE_RULES) {
      const score = rule.keywords.filter(kw => lowerText.includes(kw)).length;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = rule;
      }
    }
    if (bestMatch && bestScore > 0) {
      return bestMatch.response;
    }
    // Fuzzy fallback — check if any single word matches partially
    const words = lowerText.split(/\s+/);
    for (const rule of RESPONSE_RULES) {
      for (const word of words) {
        if (word.length >= 4 && rule.keywords.some(kw => kw.includes(word) || word.includes(kw))) {
          return rule.response;
        }
      }
    }
    return 'I\'m not sure I understand that request. Here are some things I can help with:\n\n• Account & KYC\n• Wallet & Payments\n• Exchange Rates\n• Bill Payment\n• Crowdfunding\n• Security\n\nOr type "agent" to speak with a live support agent.';
  };
  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => {
      const updated = prev.map(m => ({
        ...m,
        isActionable: false
      }));
      return [...updated, newUserMsg];
    });
    setInputValue('');
    setIsTyping(true);
    setTimeout(() => {
      const responseText = generateBotResponse(text);
      const newBotMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        sender: 'bot',
        timestamp: new Date(),
        isActionable: responseText.includes('Welcome to Billvast') || responseText.includes('live agent') || responseText.includes('things I can help')
      };
      setMessages(prev => [...prev, newBotMsg]);
      if (!isLiveAgentMode || !text.toLowerCase().includes('agent')) {
        setIsTyping(false);
      }
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    }, 800);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage(inputValue);
    }
  };
  const resetToBotMode = () => {
    setIsLiveAgentMode(false);
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text: 'You are now back to AI chat. How can I help you?',
      sender: 'bot',
      timestamp: new Date(),
      isActionable: true
    }]);
  };
  return <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end" data-id="element-322">
      <AnimatePresence data-id="element-323">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.25
      }} className="w-[340px] sm:w-[380px] h-[520px] mb-4 flex flex-col rounded-3xl border-2 border-brand-primary/20 shadow-2xl shadow-brand-primary/10 bg-white" data-id="element-324">
            {/* Header */}
            <div className="bg-brand-primary p-4 flex items-center justify-between text-white shrink-0 rounded-t-[22px]" data-id="element-325">
              <div className="flex items-center gap-3" data-id="element-326">
                <div className="relative" data-id="element-327">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm" data-id="element-328">
                    {isLiveAgentMode ? <HeadphonesIcon size={20} data-id="element-329" /> : <Bot size={20} data-id="element-330" />}
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-brand-primary rounded-full" data-id="element-331"></div>
                </div>
                <div data-id="element-332">
                  <h3 className="font-bold text-sm" data-id="element-333">Billvast Support</h3>
                  <p className="text-xs text-white/80" data-id="element-334">
                    {isLiveAgentMode ? 'Live Agent' : 'AI Assistant'} • Online
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-335">
                <X size={20} data-id="element-336" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50 space-y-4" data-id="element-337">
              {messages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-338">
                  <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-339">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${msg.sender === 'user' ? 'bg-brand-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-340">
                      {msg.sender === 'user' ? <User size={12} data-id="element-341" /> : <Bot size={12} data-id="element-342" />}
                    </div>
                    <div className={`p-3 rounded-2xl text-sm whitespace-pre-line ${msg.sender === 'user' ? 'bg-brand-primary text-white rounded-br-sm' : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm shadow-sm'}`} data-id="element-343">
                      {msg.text}
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-1 px-8" data-id="element-344">
                    {msg.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>

                  {/* Quick Actions */}
                  {msg.sender === 'bot' && msg.isActionable && (msg.text.includes('Welcome to Billvast') || msg.text.includes('back to AI chat')) && !isLiveAgentMode && <div className="flex flex-wrap gap-2 mt-3 ml-8" data-id="element-345">
                        {QUICK_ACTIONS.map(action => <button key={action} onClick={() => handleSendMessage(action)} className="text-xs px-3 py-1.5 bg-white border border-brand-primary/30 text-brand-primary-dark rounded-full hover:bg-brand-primary/5 transition-colors font-medium" data-id="element-346">
                            {action}
                          </button>)}
                      </div>}

                  {/* Fallback suggestions */}
                  {msg.sender === 'bot' && msg.isActionable && msg.text.includes('things I can help') && !isLiveAgentMode && <div className="mt-3 ml-8" data-id="element-347">
                        <button onClick={() => handleSendMessage('Talk to Agent')} className="text-xs px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors shadow-sm flex items-center gap-2 font-medium" data-id="element-348">
                          <HeadphonesIcon size={14} data-id="element-349" /> Connect to Live Agent
                        </button>
                      </div>}

                  {/* Connect to Agent */}
                  {msg.sender === 'bot' && msg.text.includes('Connecting you to a live agent') && <div className="mt-2 ml-8" data-id="element-350">
                        <span className="text-xs text-gray-400 italic" data-id="element-351">
                          Transferring to support team...
                        </span>
                      </div>}
                </div>)}

              {isTyping && <div className="flex items-end gap-2 max-w-[85%]" data-id="element-352">
                  <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center shrink-0" data-id="element-353">
                    <Bot size={12} data-id="element-354" />
                  </div>
                  <div className="p-3 bg-white border border-gray-100 rounded-2xl rounded-bl-sm shadow-sm flex gap-1" data-id="element-355">
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-356" />
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0.2
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-357" />
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0.4
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-358" />
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-359" />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-100 shrink-0 rounded-b-3xl" data-id="element-360">
              {isLiveAgentMode && <div className="mb-2 flex justify-center" data-id="element-361">
                  <button onClick={resetToBotMode} className="text-xs text-brand-primary hover:underline font-medium" data-id="element-362">
                    ← Back to AI Chat
                  </button>
                </div>}
              <div className="flex items-center gap-2" data-id="element-363">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type a message..." className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary" data-id="element-364" />
                <button onClick={() => handleSendMessage(inputValue)} disabled={!inputValue.trim()} className={`p-2.5 rounded-xl flex items-center justify-center transition-colors ${inputValue.trim() ? 'bg-brand-primary text-white hover:bg-brand-primary-dark shadow-sm' : 'bg-gray-100 text-gray-400'}`} data-id="element-365">
                  <Send size={18} data-id="element-366" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.95
    }} animate={!isOpen ? {
      y: [0, -6, 0]
    } : {}} transition={!isOpen ? {
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut'
    } : {
      duration: 0.2
    }} onClick={() => setIsOpen(!isOpen)} className="w-16 h-16 bg-brand-primary text-white rounded-full shadow-xl shadow-brand-primary/40 flex items-center justify-center relative z-50 ring-4 ring-brand-primary/20" data-id="element-367">
        <motion.div animate={{
        rotate: isOpen ? 90 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-368">
          {isOpen ? <X size={26} data-id="element-369" /> : <MessageCircle size={26} data-id="element-370" />}
        </motion.div>

        {!isOpen && unreadCount > 0 && <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-[11px] font-bold flex items-center justify-center rounded-full border-2 border-white shadow-md" data-id="element-371">
            {unreadCount}
          </motion.div>}

        {!isOpen && <motion.div initial={{
        opacity: 0,
        x: 10
      }} animate={{
        opacity: 1,
        x: 0
      }} className="absolute right-full mr-3 bg-white text-brand-text-dark text-sm font-semibold px-4 py-2 rounded-xl shadow-lg border border-gray-100 whitespace-nowrap" data-id="element-372">
            Chat with us 💬
            <div className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 bg-white border-r border-b border-gray-100 rotate-[-45deg]" data-id="element-373"></div>
          </motion.div>}
      </motion.button>
    </div>;
};